import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { api, jsonBody } from "../../api/client";
import type { ListResponse, TeamMember, Workspace, WorkspaceRole } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { authorityLabel, isOwner, membershipAllows } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Modal,
  Notice,
  StatusPill,
} from "../../ui/components";
import { SkeletonFilters, SkeletonTable } from "../../ui/skeletons";
import { useVenues } from "../venues/queries";

interface InvitationResult {
  member: TeamMember;
  invitationUrl?: string;
  expiresAt: string;
  emailDeliveryQueued: boolean;
}

// Both halves of the building offer the same fixed roles, plus the absence of one. No access is
// first because it is the answer for most people on most sides: somebody hired to run the season
// has no business committing the hall to a stranger.
const workspaceRoles: WorkspaceRole[] = [
  "none",
  "owner",
  "administrator",
  "venue_manager",
  "booking_coordinator",
  "finance",
  "operations",
  "analyst",
];

function roleLabel(role: WorkspaceRole): string {
  return role === "none" ? "No access" : titleCase(role);
}

export function TeamSettings() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const sensitive = useSensitiveAction();
  const members = useQuery({
    queryKey: ["tenant", "members"],
    queryFn: () => api<ListResponse<TeamMember>>("/api/v1/members"),
  });
  const venues = useVenues();
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [success, setSuccess] = useState("");
  const [invitation, setInvitation] = useState<InvitationResult | null>(null);
  const [suspensionTarget, setSuspensionTarget] = useState<TeamMember | null>(null);
  const [editingMembershipId, setEditingMembershipId] = useState<string | null>(null);
  const [editingAllVenues, setEditingAllVenues] = useState(false);
  const [editingVenueIds, setEditingVenueIds] = useState<string[]>([]);
  const [editorError, setEditorError] = useState<unknown>();
  const [pendingRole, setPendingRole] = useState<{
    membershipId: string;
    workspace: Workspace;
    role: WorkspaceRole;
  } | null>(null);
  const currentMembership = auth.session?.selectedMembership;
  const canManage = membershipAllows(currentMembership, "users.manage");
  const memberIsOwner = (member: TeamMember) =>
    member.eventRole === "owner" || member.bookingRole === "owner";
  const canManageMember = (member: TeamMember) =>
    canManage &&
    member.userId !== auth.session?.user.id &&
    (!memberIsOwner(member) || isOwner(currentMembership));
  const memberName = (member: TeamMember) => member.displayName || member.email;
  const rolesForMember = (member: TeamMember, workspace: Workspace) =>
    workspaceRoles.filter(
      (role) =>
        role !== "owner" ||
        isOwner(currentMembership) ||
        role === (workspace === "events" ? member.eventRole : member.bookingRole),
    );
  const invitableRoles = workspaceRoles.filter(
    (role) => role !== "owner" || isOwner(currentMembership),
  );
  const authoritySelect = (member: TeamMember, workspace: Workspace) => {
    const current = workspace === "events" ? member.eventRole : member.bookingRole;
    const selected =
      pendingRole?.membershipId === member.membershipId && pendingRole.workspace === workspace
        ? pendingRole.role
        : current;
    return (
      <select
        className="employee-role-select"
        aria-label={`${workspace === "events" ? "Events" : "Bookings"} role for ${memberName(member)}`}
        value={selected}
        disabled={!canManageMember(member) || !sensitive.unlocked || working}
        onChange={(event) =>
          void updateRole(member, workspace, event.target.value as WorkspaceRole)
        }
      >
        {rolesForMember(member, workspace).map((role) => (
          <option key={role} value={role}>
            {roleLabel(role)}
          </option>
        ))}
      </select>
    );
  };

  function captureActionError(caught: unknown) {
    if (!sensitive.markProtectedError(caught)) setError(caught);
  }

  function closeEditor() {
    setEditingMembershipId(null);
    setEditorError(undefined);
  }

  function openEditor(member: TeamMember) {
    setError(undefined);
    setSuccess("");
    setEditorError(undefined);
    setEditingAllVenues(member.allVenues);
    setEditingVenueIds(member.venueIds);
    setEditingMembershipId(member.membershipId);
  }

  function cacheMember(updated: TeamMember) {
    queryClient.setQueryData<ListResponse<TeamMember>>(["tenant", "members"], (current) =>
      current
        ? {
            ...current,
            items: current.items.map((member) =>
              member.membershipId === updated.membershipId ? updated : member,
            ),
          }
        : current,
    );
  }

  async function persistAuthority(
    member: TeamMember,
    input: Pick<TeamMember, "eventRole" | "bookingRole" | "allVenues" | "venueIds" | "roleVersion">,
    onFailure: (caught: unknown) => void,
  ): Promise<TeamMember | null> {
    setWorking(true);
    setError(undefined);
    setSuccess("");
    try {
      const updated = await api<TeamMember>(`/api/v1/members/${member.membershipId}/authority`, {
        method: "PUT",
        body: jsonBody(input),
      });
      cacheMember(updated);
      return updated;
    } catch (caught) {
      if (sensitive.markProtectedError(caught)) closeEditor();
      else onFailure(caught);
      return null;
    } finally {
      setWorking(false);
    }
  }

  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const allVenues = form.get("allVenues") === "on";
    const venueIds = allVenues ? [] : form.getAll("venueIds").map(String);
    const eventRole = String(form.get("eventRole")) as WorkspaceRole;
    const bookingRole = String(form.get("bookingRole")) as WorkspaceRole;
    setError(undefined);
    setSuccess("");
    setInvitation(null);
    if (eventRole === "none" && bookingRole === "none") {
      setError(new Error("Give this person a role in at least one workspace."));
      return;
    }
    if (!allVenues && venueIds.length === 0) {
      setError(new Error("Choose at least one venue when Access all venues is not selected."));
      return;
    }
    setWorking(true);
    try {
      const result = await api<InvitationResult>("/api/v1/members/invitations", {
        method: "POST",
        body: jsonBody({
          email: String(form.get("email")),
          name: String(form.get("name")),
          eventRole,
          bookingRole,
          allVenues,
          venueIds,
        }),
      });
      setInvitation(result);
      formElement.reset();
      await members.refetch();
    } catch (caught) {
      captureActionError(caught);
    } finally {
      setWorking(false);
    }
  }

  async function updateRole(member: TeamMember, workspace: Workspace, role: WorkspaceRole) {
    const eventRole = workspace === "events" ? role : member.eventRole;
    const bookingRole = workspace === "bookings" ? role : member.bookingRole;
    if (eventRole === member.eventRole && bookingRole === member.bookingRole) return;
    // No access on both sides would be an account that can sign in and do nothing. Suspending says
    // that already, reversibly and with a reason attached, so it is refused here as well as by the
    // server rather than being reported as a validation failure after the fact.
    if (eventRole === "none" && bookingRole === "none") {
      setError(
        new Error(
          `${memberName(member)} would be left with no access anywhere. Suspend the employee instead of removing both workspaces.`,
        ),
      );
      return;
    }
    setPendingRole({ membershipId: member.membershipId, workspace, role });
    const updated = await persistAuthority(
      member,
      {
        eventRole,
        bookingRole,
        allVenues: member.allVenues,
        venueIds: member.venueIds,
        roleVersion: member.roleVersion,
      },
      setError,
    );
    setPendingRole(null);
    if (updated) {
      setSuccess(
        `${memberName(member)} is now ${authorityLabel(updated.eventRole, updated.bookingRole)}.`,
      );
    }
  }

  async function updateVenueAccess(event: FormEvent<HTMLFormElement>, member: TeamMember) {
    event.preventDefault();
    setEditorError(undefined);
    if (!editingAllVenues && editingVenueIds.length === 0) {
      setEditorError(
        new Error("Choose at least one venue when Access all venues is not selected."),
      );
      return;
    }
    const updated = await persistAuthority(
      member,
      {
        eventRole: member.eventRole,
        bookingRole: member.bookingRole,
        allVenues: editingAllVenues,
        venueIds: editingAllVenues ? [] : editingVenueIds,
        roleVersion: member.roleVersion,
      },
      setEditorError,
    );
    if (updated) {
      setSuccess(`${memberName(member)} venue access was updated.`);
      closeEditor();
    }
  }

  async function setStatus(member: TeamMember, status: "active" | "suspended") {
    setWorking(true);
    setError(undefined);
    setSuccess("");
    try {
      await api(`/api/v1/members/${member.membershipId}/status`, {
        method: "PUT",
        body: jsonBody({
          status,
          reason:
            status === "suspended"
              ? "Access suspended by tenant administrator"
              : "Access restored by tenant administrator",
          roleVersion: member.roleVersion,
        }),
      });
      await members.refetch();
      setSuccess(`${memberName(member)} was ${status === "suspended" ? "suspended" : "restored"}.`);
    } catch (caught) {
      captureActionError(caught);
    } finally {
      setWorking(false);
      if (status === "suspended") setSuspensionTarget(null);
    }
  }

  if (members.isLoading || venues.isLoading)
    return (
      <div className="stack">
        <SkeletonFilters count={4} />
        <SkeletonTable columns={6} rows={4} />
      </div>
    );
  if (members.error || venues.error) return <ErrorPanel error={members.error ?? venues.error} />;
  const items = members.data?.items ?? [];
  const editingMember = items.find((member) => member.membershipId === editingMembershipId) ?? null;

  return (
    <div className="stack">
      {canManage ? sensitive.view : null}
      {error ? <ErrorPanel error={error} /> : null}
      {success ? <Notice tone="success">{success}</Notice> : null}
      {invitation ? (
        invitation.emailDeliveryQueued ? (
          <Notice tone="success" title="Invitation email queued">
            The employee invitation is ready for delivery. It can only be used once and expires{" "}
            {new Date(invitation.expiresAt).toLocaleString()}.
          </Notice>
        ) : (
          <Notice tone="warning" title="Invitation created, but email was not queued">
            <span>
              Send this private fallback link to the employee securely:{" "}
              <a href={invitation.invitationUrl}>{invitation.invitationUrl}</a>. It can only be used
              once and expires {new Date(invitation.expiresAt).toLocaleString()}.
            </span>
          </Notice>
        )
      ) : null}
      {canManage ? (
        <Card>
          <CardHeader
            title="Invite employee"
            description="Give this person a role in each workspace, choosing No access for a side they should not enter, then decide whether they work with every venue or only selected venues."
          />
          <form className="form-grid" onSubmit={invite}>
            <Field label="Name">
              <input name="name" required minLength={2} />
            </Field>
            <Field label="Email">
              <input name="email" type="email" required />
            </Field>
            <Field label="Events role">
              <select name="eventRole" defaultValue="booking_coordinator">
                {invitableRoles.map((role) => (
                  <option key={role} value={role}>
                    {roleLabel(role)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Bookings role">
              <select name="bookingRole" defaultValue="none">
                {invitableRoles.map((role) => (
                  <option key={role} value={role}>
                    {roleLabel(role)}
                  </option>
                ))}
              </select>
            </Field>
            <label className="check-field">
              <input name="allVenues" type="checkbox" defaultChecked /> Access all venues
            </label>
            <fieldset className="full">
              <legend>Selected venues (used when Access all venues is not selected)</legend>
              <div className="form-grid">
                {venues.data?.items.map((venue) => (
                  <label className="check-field" key={venue.id}>
                    <input name="venueIds" value={venue.id} type="checkbox" /> {venue.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="full">
              <FormActions
                submitting={working}
                disabled={!sensitive.unlocked}
                submitLabel="Create invitation"
              />
            </div>
          </form>
        </Card>
      ) : (
        <Notice tone="warning">Your role can view the team but cannot change access.</Notice>
      )}
      <Card>
        <CardHeader
          title="Employees"
          description="Change fixed roles in the table or open one focused editor for venue access."
        />
        {items.length ? (
          <DataTable
            caption="Employees"
            items={items}
            getKey={(member) => member.membershipId}
            columns={[
              {
                label: "Employee",
                render: (member) => (
                  <>
                    <strong>{member.displayName || "Invited employee"}</strong>
                    <br />
                    <small>{member.email}</small>
                  </>
                ),
              },
              {
                label: "Events",
                render: (member) => authoritySelect(member, "events"),
              },
              {
                label: "Bookings",
                render: (member) => authoritySelect(member, "bookings"),
              },
              {
                label: "Venues",
                render: (member) =>
                  member.allVenues ? "All venues" : `${member.venueIds.length} selected`,
              },
              {
                label: "Status",
                render: (member) => <StatusPill value={member.membershipStatus} />,
              },
              {
                label: "Actions",
                render: (member) => {
                  const name = memberName(member);
                  if (!canManageMember(member)) {
                    return (
                      <small>
                        {member.userId === auth.session?.user.id
                          ? "Current account"
                          : "Not available"}
                      </small>
                    );
                  }
                  return (
                    <div className="inline employee-actions">
                      <button
                        className="button secondary small"
                        type="button"
                        aria-label={`Edit ${name} venue access`}
                        disabled={working || !sensitive.unlocked}
                        onClick={() => openEditor(member)}
                      >
                        Edit
                      </button>
                      {member.membershipStatus === "suspended" ? (
                        <button
                          className="button secondary small"
                          type="button"
                          disabled={working || !sensitive.unlocked}
                          onClick={() => void setStatus(member, "active")}
                        >
                          Restore
                        </button>
                      ) : (
                        <button
                          className="button secondary small"
                          type="button"
                          disabled={working || !sensitive.unlocked}
                          onClick={() => setSuspensionTarget(member)}
                        >
                          Suspend
                        </button>
                      )}
                    </div>
                  );
                },
              },
            ]}
          />
        ) : (
          <EmptyState
            title="No employees"
            description="Invite the first employee to begin delegating work."
          />
        )}
      </Card>
      {editingMember ? (
        <Modal
          open
          wide
          title={`Edit venue access for ${memberName(editingMember)}`}
          description="Choose whether this employee can work with every venue or only specific venues. Change their fixed role in the employee table."
          onClose={() => {
            if (!working) closeEditor();
          }}
        >
          {editorError ? <ErrorPanel error={editorError} /> : null}
          <form
            className="form-stack"
            onSubmit={(event) => updateVenueAccess(event, editingMember)}
          >
            <label className="check-field">
              <input
                name="allVenues"
                type="checkbox"
                checked={editingAllVenues}
                disabled={working}
                onChange={(event) => setEditingAllVenues(event.target.checked)}
              />{" "}
              Access all venues
            </label>
            <fieldset disabled={working || editingAllVenues}>
              <legend>Specific venues</legend>
              <div className="form-grid">
                {venues.data?.items.map((venue) => (
                  <label className="check-field" key={venue.id}>
                    <input
                      name="venueIds"
                      value={venue.id}
                      type="checkbox"
                      checked={editingVenueIds.includes(venue.id)}
                      onChange={(event) =>
                        setEditingVenueIds((current) =>
                          event.target.checked
                            ? [...current, venue.id]
                            : current.filter((venueId) => venueId !== venue.id),
                        )
                      }
                    />{" "}
                    {venue.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <FormActions
              submitting={working}
              disabled={!sensitive.unlocked}
              submitLabel="Save venue access"
              onCancel={closeEditor}
            />
          </form>
        </Modal>
      ) : null}
      <ConfirmDialog
        open={suspensionTarget !== null}
        title="Suspend employee access?"
        description={
          suspensionTarget
            ? `${memberName(suspensionTarget)} will be signed out and unable to open this organization until an administrator restores access.`
            : "The employee will be signed out and unable to return until access is restored."
        }
        confirmLabel="Suspend access"
        submitting={working}
        disabled={!sensitive.unlocked}
        onClose={() => setSuspensionTarget(null)}
        onConfirm={() => {
          if (suspensionTarget) void setStatus(suspensionTarget, "suspended");
        }}
      />
    </div>
  );
}
